import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Hook that automatically scrolls to the top when the route changes
 * This ensures that users start at the top of each new page they navigate to
 */
export const useScrollToTop = () => {
  const location = useLocation()
  const prevPathname = useRef(location.pathname)

  useEffect(() => {
    // Only scroll if the pathname actually changed
    if (prevPathname.current !== location.pathname) {
      // Scroll window to top
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant', // Use 'instant' for immediate scroll without animation
      })

      // Also scroll the main content area if it exists
      const mainContent = document.querySelector('main')

      if (mainContent) {
        mainContent.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant',
        })
      }

      // Scroll any other scrollable containers that might be present
      const scrollableContainers = document.querySelectorAll(
        '[class*="overflow-y-scroll"], [class*="overflow-auto"]',
      )

      scrollableContainers.forEach(container => {
        if (container instanceof HTMLElement) {
          container.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant',
          })
        }
      })

      // Update the previous pathname
      prevPathname.current = location.pathname
    }
  }, [location.pathname])
}

/**
 * Hook that provides a manual scroll to top function
 * Useful for buttons or other UI elements that need to scroll to top
 */
export const useManualScrollToTop = () => {
  const scrollToTop = useCallback(() => {
    // Scroll window to top
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth', // Use 'smooth' for manual scroll with animation
    })

    // Also scroll the main content area if it exists
    const mainContent = document.querySelector('main')

    if (mainContent) {
      mainContent.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }

    // Scroll any other scrollable containers that might be present
    const scrollableContainers = document.querySelectorAll(
      '[class*="overflow-y-scroll"], [class*="overflow-auto"]',
    )

    scrollableContainers.forEach(container => {
      if (container instanceof HTMLElement) {
        container.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      }
    })
  }, [])

  return scrollToTop
}
